import { FC, useEffect, useMemo, useState } from "react";
import { AppointmentData } from "../../models";
import { AutocompleteTextField, CheckInputField } from "../../components/Forms/fields";
import { useTranslations } from "../../store/translations/hooks";
import { TK } from "../../store/translations/translationKeys";
import React from "react";
import Delete from '@material-ui/icons/Delete'
import { useEntitiesData } from "../../store/entities/hooks";
import { getGeneralSettings } from "../../store/settings/selectors";
import { useSelector } from "react-redux";
import { serviceName } from "../../shared/appointmint/appointment";
import { filterDefinitions, FilterContext, FilterInstance, serializeFilters, deserializeFilters } from "./filters";
import CollapsableItem from "../../components/CollapsableItem";
import { useHistory } from "react-router-dom";
import AppButton from '../../components/AppButton';

function readFiltersFromUrl() {
    const raw = new URLSearchParams(window.location.search).get('f');
    if (!raw) return { filters: [] as FilterInstance[], invert: false };
    try { return deserializeFilters(raw); }
    catch { return { filters: [] as FilterInstance[], invert: false }; }
}

export const AppointmentsFilters: FC<{
    onAppointmentsFiltered: (appointments: AppointmentData[]) => void,
}> = ({
    onAppointmentsFiltered,
}) => {
    const t = useTranslations()
    const settings = useSelector(getGeneralSettings)
    const history = useHistory()
    const [filters, setFilters] = useState<FilterInstance[]>(() => readFiltersFromUrl().filters)
    const [invertFilterResult, setInvertFilterResult] = useState(() => readFiltersFromUrl().invert)
    const { searchResult, items } = useEntitiesData<AppointmentData>('appointments')

    const labelByType = useMemo(
        () => Object.fromEntries(filterDefinitions.map(d => [d.type, t(d.labelKey)])),
        [t]
    )

    const typeByLabel = useMemo(
        () => Object.fromEntries(filterDefinitions.map(d => [t(d.labelKey), d.type])),
        [t]
    )

    const serviceNameFn = (type: string) => serviceName(type, settings)

    const ctx: FilterContext = useMemo(() => ({
        branches: Object.keys(settings.branches ?? {}),
        types: [...new Set(Object.values(items).map(a => a.type).filter((a): a is string => !!a).map(serviceNameFn))],
        professionals: [...new Set(Object.values(items).map(a => a.professionalName).filter((a): a is string => !!a))],
        clientNames: [...new Set(Object.values(items).map(a => a.clientName).filter((a): a is string => !!a))],
        t: t,
        serviceNameFn,
    }), [settings, items, t])

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (filters.length > 0 || invertFilterResult) {
            params.set('f', serializeFilters(filters, invertFilterResult));
        } else {
            params.delete('f');
        }
        history.replace({ search: params.toString() });
    }, [filters, invertFilterResult])

    useEffect(() => {
        const appointments = searchResult.map(id => items[id])

        if (filters.length === 0) {
            onAppointmentsFiltered(invertFilterResult ? [] : appointments)
            return
        }

        const resolvedFilters = filters.map(filter => {
            const def = filterDefinitions.find(d => d.type === filter.type)
            if (!def) return () => false
            if (def.resolve) return def.resolve(filter.values, appointments, ctx)
            return (a: AppointmentData) => def.predicate!(a, filter.values, ctx)
        })

        const filtered = appointments.filter(a => resolvedFilters.some(f => f(a)))
        onAppointmentsFiltered(invertFilterResult ? appointments.filter(a => !filtered.includes(a)) : filtered)
    }, [filters, searchResult, invertFilterResult, ctx])

    const title = filters.length > 0
        ? `${t(TK.filters)} (${filters.length})`
        : t(TK.filters)

    return (
        <CollapsableItem title={title} size="sm" headerAlign="right">
            <>
                {filters.map((filter, i) => {
                    const def = filterDefinitions.find(d => d.type === filter.type)
                    return (
                        <div key={i} style={{ display: 'flex' }}>
                            <div style={{ flex: 1 }}>
                                <AutocompleteTextField
                                    id={`filter-${i}-type`}
                                    value={labelByType[filter.type]}
                                    options={Object.values(labelByType)}
                                    onValueChanged={label => {
                                        const newFilters = [...filters]
                                        newFilters[i] = { type: typeByLabel[label], values: [] }
                                        setFilters(newFilters)
                                    }}
                                />
                                {def?.hasValues && (
                                    <AutocompleteTextField
                                        id={`filter-${i}-values`}
                                        multiSelect
                                        values={filter.values}
                                        options={def.getOptions?.(ctx) ?? []}
                                        onValuesChanged={values => {
                                            const newFilters = [...filters]
                                            newFilters[i] = { ...newFilters[i], values }
                                            setFilters(newFilters)
                                        }}
                                        placeholder={t(TK.filter)}
                                    />
                                )}
                                {i < filters.length - 1 && <div>{t(TK.or)}</div>}
                            </div>
                            <AppButton
                                color="danger"
                                outline
                                className="ml-2 h-100"
                                onClick={() => setFilters(filters.filter((_, idx) => idx !== i))}
                            >
                                <Delete />
                            </AppButton>
                        </div>
                    )
                })}
                {filters.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginRight: '4rem', marginTop: 'var(--spacer-XXS)' }}>
                        <CheckInputField
                            label={t(TK.invertFilterResult)}
                            value={invertFilterResult}
                            onChange={setInvertFilterResult}
                        />
                    </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <AppButton className="mt-2" onClick={() => setFilters([...filters, { type: 'appointmentsWith', values: [] }])}>
                        {t(TK.addFilter)}
                    </AppButton>
                </div>
            </>
        </CollapsableItem>
    )
}
